/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useContext, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { useIsSticky } from '../../hooks/useIsSticky';
import { SearchCategory } from '../../types/enums';
import { Kfh_PropertySearchMetaType, Kfh_SearchFacetType } from '../../types/graphqlTypes';
import { getOptions } from '../../utils/getOptions';
import { AltSelect } from '../forms/AltSelect';
import { Radio } from '../forms/Radio';
import { StyledPropertySearch, StyledPropertySearchSection } from './PropertySearch';
import { Filters } from '../icon/icons/Filters';
import { Button } from '../Button';
import { UiStateContext } from '../../context/UiStateContext';
import { Skeleton, StyleSkeletonOverlay } from '../Skeleton';

interface PropertySearchProps {
  parameters: Kfh_PropertySearchMetaType;
  facets: Kfh_SearchFacetType;
  category: SearchCategory;
  isLoading?: boolean;
}

const StyledRadioGroup = styled.div`
  ${({ theme: { space, colors } }) => css`
    display: flex;
    gap: ${space.md};
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding-right: ${space.lg};
  `}
`;

export const StyledCommercialButton = styled(Button)`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.lg}) {
      display: none;
    }
  `}
`;

const StyledInlineFilters = styled.div`
  ${({ theme: { space, media } }) => css`
    display: none;

    @media (min-width: ${media.md}) {
      display: flex;
      flex-grow: 1;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: ${space.sm};

      > *:nth-child(n + 2) {
        display: none;
      }
    }

    @media (min-width: ${media.lg}) {
      > *:nth-child(n + 2) {
        display: block;
      }
    }
  `}
`;

export const PropertySearchCommercial: FC<PropertySearchProps> = ({ parameters, facets, isLoading }) => {
  const { register, watch, control } = useFormContext();

  const { isSticky, stickyRef } = useIsSticky();

  const filterBladeTriggerRef = useRef<HTMLButtonElement>();
  const { setActiveBlade, setActiveBladeTriggerEl } = useContext(UiStateContext);
  const handleOpenFilters = () => {
    setActiveBlade('propertyFilters');
    setActiveBladeTriggerEl(filterBladeTriggerRef);
  };

  const propertyTypes =
    facets?.propertyType?.length >= 1
      ? facets.propertyType.map(({ value, facet }) => ({ value: value || facet, name: facet }))
      : [];
  const areas =
    facets?.areas?.length >= 1 ? facets.areas.map(({ value, facet }) => ({ value: value || facet, name: facet })) : [];

  return (
    <StyledPropertySearchSection
      isSticky={isSticky}
      contained
      tight
      disablePaddingTop
      ref={stickyRef}
      className="section--white"
    >
      {isLoading && (
        <StyleSkeletonOverlay height="64px" isSticky={isSticky}>
          <Skeleton height="64px" />
        </StyleSkeletonOverlay>
      )}
      <StyledPropertySearch>
        <>
          <StyledRadioGroup>
            <Radio label="Buy" id="buy" {...register('category')} value={SearchCategory.Resale} />
            <Radio label="Rent" id="rent" {...register('category')} value={SearchCategory.Rental} />
          </StyledRadioGroup>
          <StyledInlineFilters>
            <Controller
              name="commercialArea"
              control={control}
              render={({ field }) => (
                <AltSelect
                  id="commercialArea"
                  label="Location"
                  {...field}
                  options={[{ value: '', name: 'All' }, ...areas]}
                  constrained
                />
              )}
            />

            <AltSelect
              id="commercialSize"
              label="Min size (Sq.m)"
              {...register('commercialSize', {
                valueAsNumber: true,
              })}
              options={getOptions(parameters?.commercialMinimumSize)}
              constrained
            />
            <AltSelect
              id="commercialSizemax"
              label="Max size (Sq.m)"
              {...register('commercialSizemax', {
                valueAsNumber: true,
              })}
              options={getOptions(parameters?.commercialMaximumSize)}
              constrained
            />

            <Controller
              name="commercialPropertyType"
              control={control}
              render={({ field }) => (
                <AltSelect
                  id="commercialPropertyType"
                  label="Property type"
                  {...field}
                  options={[{ value: '', name: 'All' }, ...propertyTypes]}
                  constrained
                />
              )}
            />
          </StyledInlineFilters>
          <StyledCommercialButton
            onClick={handleOpenFilters}
            type="button"
            ref={filterBladeTriggerRef}
            iconRight={<Filters />}
          >
            Filters
          </StyledCommercialButton>
        </>
      </StyledPropertySearch>
    </StyledPropertySearchSection>
  );
};
